import { useCvLanguage } from '../cvLanguage';
import React, { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, IconButton, TextField, Typography } from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import ShortTextRounded from '@mui/icons-material/ShortTextRounded';
import WorkOutlineRounded from '@mui/icons-material/WorkOutlineRounded';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import TranslateRounded from '@mui/icons-material/TranslateRounded';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import CenterFocusStrongOutlined from '@mui/icons-material/CenterFocusStrongOutlined';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
const listSections = [{
  key: 'experience',
  title: 'Experiencia',
  singular: 'experiencia',
  icon: WorkOutlineRounded,
  fields: [['title', 'Cargo'], ['company', 'Empresa'], ['city', 'Ciudad'], ['startDate', 'Inicio'], ['endDate', 'Finalización']],
  description: 'description'
}, {
  key: 'education',
  title: 'Educación',
  singular: 'estudio',
  icon: SchoolOutlined,
  fields: [['degree', 'Grado o título'], ['institution', 'Institución'], ['city', 'Ciudad'], ['startDate', 'Inicio'], ['endDate', 'Finalización']],
  description: 'details'
}, {
  key: 'skills',
  title: 'Habilidades',
  singular: 'habilidad',
  icon: AutoAwesomeOutlined
}, {
  key: 'courses',
  title: 'Cursos',
  singular: 'curso',
  icon: VerifiedOutlined,
  fields: [['title', 'Título del curso'], ['date', 'Fecha']]
}, {
  key: 'languages',
  title: 'Idiomas',
  singular: 'idioma',
  icon: TranslateRounded,
  fields: [['name', 'Idioma'], ['level', 'Nivel']]
}, {
  key: 'research',
  title: 'Investigación',
  singular: 'investigación',
  icon: ScienceOutlined,
  fields: [['title', 'Título'], ['company', 'Organización'], ['city', 'Ciudad'], ['startDate', 'Inicio'], ['endDate', 'Finalización']],
  description: 'description',
  optional: true
}, {
  key: 'areasOfSpecialization',
  title: 'Especialización',
  singular: 'especialización',
  icon: CenterFocusStrongOutlined,
  fields: [['title', 'Título'], ['institution', 'Institución'], ['date', 'Fecha']],
  optional: true
}];
const sections = [{
  key: 'personalInfo',
  title: 'Información personal',
  icon: PersonOutlineRounded
}, {
  key: 'introduction',
  title: 'Perfil profesional',
  icon: ShortTextRounded
}, ...listSections];
const personalFields = [['name', 'Nombre completo', 'text', 'name'], ['email', 'Correo electrónico', 'email', 'email'], ['phone', 'Teléfono', 'tel', 'tel'], ['city', 'Ciudad', 'text', 'address-level2'], ['linkedin', 'LinkedIn', 'text', 'off'], ['github', 'GitHub', 'text', 'off']];
const newEntry = section => section.key === 'skills' ? '' : {
  ...Object.fromEntries(section.fields.map(([key]) => [key, ''])),
  ...(section.description ? {
    [section.description]: ['']
  } : {})
};
function Field({
  label,
  value,
  onChange,
  ...props
}) {
  return <TextField label={label} value={value ?? ''} onChange={e => onChange(e.target.value)} fullWidth variant="outlined" {...props} />;
}
function ListEditor({
  section,
  items,
  update
}) {
  const { t } = useCvLanguage();
  const change = (index, key, value) => update(items.map((item, i) => i === index ? typeof item === 'string' ? value : {
    ...item,
    [key]: value
  } : item));
  const move = (index, step) => {
    const next = [...items];
    [next[index], next[index + step]] = [next[index + step], next[index]];
    update(next);
  };
  return <Box className="cv-entry-list">
    {items.map((item, index) => {
      const title = typeof item === 'string' ? t("Habilidad {number}", { number: index + 1 }) : item.degree || item.title || item.name || t("Nueva {section}", { section: t(section.singular) });
      return <Box className="cv-entry-panel" key={index}>
        <Box className="cv-entry-heading"><Box><Typography component="h3">{title}</Typography><Typography variant="caption" color="text.secondary">{typeof item === 'object' ? item.institution || item.company || t("{index} de {total}", { index: index + 1, total: items.length }) : t("{index} de {total}", { index: index + 1, total: items.length })}</Typography></Box>
          <Box className="cv-entry-actions">
            {items.length > 1 && <><IconButton aria-label={t("Subir {title}", { title })} disabled={index === 0} onClick={() => move(index, -1)}><ArrowUpwardRounded fontSize="small" /></IconButton><IconButton aria-label={t("Bajar {title}", { title })} disabled={index === items.length - 1} onClick={() => move(index, 1)}><ArrowDownwardRounded fontSize="small" /></IconButton></>}
            <IconButton aria-label={t("Eliminar {title}", { title })} onClick={() => update(items.filter((_, i) => i !== index))}><DeleteOutlineRounded fontSize="small" /></IconButton>
          </Box>
        </Box>
        <Box className="cv-fields">
          {section.key === 'skills' ? <Field label={t("Habilidad")} value={item} onChange={value => change(index, null, value)} /> : section.fields.map(([key, label]) => <Box key={key} className={['startDate', 'endDate', 'date', 'level'].includes(key) ? '' : 'cv-field-wide'}><Field label={t(label)} value={item[key]} onChange={value => change(index, key, value)} /></Box>)}
        </Box>
        {section.description && <Box className="cv-description-points"><Typography component="h4">{section.key === 'education' ? t("Descripción y logros") : t("Responsabilidades y logros")}</Typography>
          {(item[section.description] || []).map((point, pointIndex) => <Box className="cv-point" key={pointIndex}><Field label={t("Descripción {number}", { number: pointIndex + 1 })} value={point} multiline minRows={2} onChange={value => change(index, section.description, item[section.description].map((v, i) => i === pointIndex ? value : v))} /><IconButton aria-label={t("Eliminar descripción {number}", { number: pointIndex + 1 })} onClick={() => change(index, section.description, item[section.description].filter((_, i) => i !== pointIndex))}><DeleteOutlineRounded fontSize="small" /></IconButton></Box>)}
          <Button startIcon={<AddRounded />} onClick={() => change(index, section.description, [...(item[section.description] || []), ''])}>{t("Añadir punto")}</Button>
        </Box>}
      </Box>;
    })}
    {!items.length && <Typography className="cv-empty-section" color="text.secondary">{section.optional ? t("Incluye esta sección si aporta a tu perfil.") : t("Añade tu {section} para incluirla en el currículum.", { section: t(section.singular) })}</Typography>}
    <Button variant="outlined" startIcon={<AddRounded />} onClick={() => update([...items, newEntry(section)])} className="cv-add-entry">{t("Añadir {section}", { section: t(section.singular) })}</Button>
  </Box>;
}
export default function CvSectionEditor({
  data,
  updateCvData
}) {
  const { t } = useCvLanguage();
  const [expanded, setExpanded] = useState(data.personalInfo?.name ? false : 'personalInfo');
  const update = (key, value) => updateCvData(previous => ({
    ...previous,
    [key]: value
  }));
  const summary = section => section.key === 'personalInfo' ? data.personalInfo?.name || t("Nombre y datos de contacto") : section.key === 'introduction' ? data.introduction ? t("Tu resumen profesional") : t("Cuenta brevemente tu trayectoria") : data[section.key]?.length ? `${data[section.key].length} ${data[section.key].length === 1 ? t("entrada") : t("entradas")}` : section.optional ? t("Opcional") : t("Sin completar");
  const filled = section => section.key === 'personalInfo' ? Boolean(data.personalInfo?.name) : section.key === 'introduction' ? Boolean(data.introduction) : Boolean(data[section.key]?.length);
  return <Box className="cv-section-list" aria-label={t("Secciones del currículum")}>
    {sections.map(section => {
      const Icon = section.icon;
      return <Accordion key={section.key} expanded={expanded === section.key} onChange={(_, open) => setExpanded(open ? section.key : false)} disableGutters elevation={0} className="cv-section" TransitionProps={{
        unmountOnExit: true
      }}>
      <AccordionSummary expandIcon={<ExpandMoreRounded />} id={`cv-${section.key}-header`} aria-controls={`cv-${section.key}-content`}>
        <Icon className="cv-section-icon" /><Box className="cv-section-copy"><Typography component="h2">{t(section.title)}</Typography><Typography variant="caption" color="text.secondary">{summary(section)}</Typography></Box>{filled(section) && <CheckRounded className="cv-section-check" aria-label={t("Con contenido")} />}
      </AccordionSummary>
      <AccordionDetails id={`cv-${section.key}-content`}>
        {section.key === 'personalInfo' ? <Box className="cv-fields">{personalFields.map(([key, label, type, autoComplete]) => <Box key={key} className={key === 'name' ? 'cv-field-wide' : ''}><Field label={t(label)} type={type} autoComplete={autoComplete} value={data.personalInfo?.[key]} onChange={value => updateCvData(previous => ({
                ...previous,
                personalInfo: {
                  ...previous.personalInfo,
                  [key]: value
                }
              }))} /></Box>)}</Box> : section.key === 'introduction' ? <Field label={t("Sobre tu experiencia")} value={data.introduction} multiline minRows={5} helperText={t("Resume tu especialidad, experiencia y lo que aportas.")} onChange={value => update('introduction', value)} /> : <ListEditor section={section} items={data[section.key] || []} update={value => update(section.key, value)} />}
      </AccordionDetails>
    </Accordion>;
    })}
  </Box>;
}
